import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';
import { DemoDataService } from 'src/shared/services/demo-data.service';

// E-signing is powered by Firma (firma.dev). Firma's API speaks "signing
// requests"; our UI calls them "envelopes". This mock mirrors the action-style
// API (list-envelopes / list-templates / send-envelope / get-status /
// get-download-url) so it can be wired to a real `/api/firma` dispatcher later.

export type FirmaStatus = 'not_sent' | 'in_progress' | 'finished' | 'declined' | 'expired' | 'cancelled';

/** One step in an envelope's signing history. Mirrors rent-collection's payment timeline. */
export interface FirmaEvent {
  label: string;
  date: string;
  done: boolean;
}

export interface FirmaEnvelope {
  id: string;
  name: string;
  template_name: string;
  recipient_name: string;
  recipient_email: string;
  property_name: string;
  unit: string;
  status: FirmaStatus;
  created_at: string;
  sent_at?: string;
  completed_at?: string;
  recipient_id: string;
  download_url?: string;
  timeline: FirmaEvent[];
}

export interface FirmaTemplate {
  id: string;
  name: string;
  description: string;
  updated_at: string;
  category: string;
  /** Fillable fields (name, dates, rent amount, …) the sender completes before sending. */
  fields: number;
  signers: number;
  times_used: number;
  last_used_at?: string;
}

// The demo world is shared with rent-collection / rental-units / rental-applications —
// same properties, units and tenants, so a document opened here matches the tenant the
// user just looked at on Rent Collection.
const DUMMY_ENVELOPES: FirmaEnvelope[] = [
  {
    id: 'env_1', name: 'Lease Agreement — Oakwood Apartments Apt 3B', template_name: 'Standard Lease Agreement',
    recipient_name: 'Priya Raman', recipient_email: 'priya.raman@email.com',
    property_name: 'Oakwood Apartments', unit: 'Apt 3B',
    status: 'in_progress', created_at: '2026-07-22', sent_at: '2026-07-23', recipient_id: 'r_a1b2c3',
    timeline: [
      { label: 'Document created', date: '2026-07-22', done: true },
      { label: 'Sent to Priya Raman', date: '2026-07-23', done: true },
      { label: 'Opened by recipient', date: '2026-07-24', done: true },
      { label: 'Signed', date: '', done: false },
    ],
  },
  {
    id: 'env_2', name: 'Pet Addendum — Oakwood Apartments Apt 3B', template_name: 'Pet Addendum',
    recipient_name: 'Priya Raman', recipient_email: 'priya.raman@email.com',
    property_name: 'Oakwood Apartments', unit: 'Apt 3B',
    status: 'finished', created_at: '2026-06-27', sent_at: '2026-06-28', completed_at: '2026-06-30',
    recipient_id: 'r_d4e5f6', download_url: 'https://app.firma.dev/download/env_2',
    timeline: [
      { label: 'Document created', date: '2026-06-27', done: true },
      { label: 'Sent to Priya Raman', date: '2026-06-28', done: true },
      { label: 'Opened by recipient', date: '2026-06-29', done: true },
      { label: 'Signed', date: '2026-06-30', done: true },
    ],
  },
  {
    id: 'env_3', name: 'Lease Renewal — Cedar Court Unit 7', template_name: 'Lease Renewal',
    recipient_name: 'The Alvarez Family', recipient_email: 'alvarez@email.com',
    property_name: 'Cedar Court', unit: 'Unit 7',
    status: 'in_progress', created_at: '2026-07-18', sent_at: '2026-07-20', recipient_id: 'r_g7h8i9',
    timeline: [
      { label: 'Document created', date: '2026-07-18', done: true },
      { label: 'Sent to The Alvarez Family', date: '2026-07-20', done: true },
      { label: 'Opened by recipient', date: '', done: false },
      { label: 'Signed', date: '', done: false },
    ],
  },
  {
    id: 'env_4', name: 'Move-In Inspection — Riverside Townhomes Unit 4', template_name: 'Move-In / Out Checklist',
    recipient_name: '', recipient_email: '',
    property_name: 'Riverside Townhomes', unit: 'Unit 4',
    status: 'not_sent', created_at: '2026-07-29', recipient_id: '',
    timeline: [
      { label: 'Document created', date: '2026-07-29', done: true },
      { label: 'Sent to recipient', date: '', done: false },
      { label: 'Signed', date: '', done: false },
    ],
  },
  {
    id: 'env_5', name: 'Notice to Vacate — Downtown Loft Unit 5', template_name: 'Notice to Vacate',
    recipient_name: 'Grace Kim', recipient_email: 'grace.kim@email.com',
    property_name: 'Downtown Loft', unit: 'Unit 5',
    status: 'finished', created_at: '2026-07-05', sent_at: '2026-07-06', completed_at: '2026-07-07',
    recipient_id: 'r_j1k2l3', download_url: 'https://app.firma.dev/download/env_5',
    timeline: [
      { label: 'Document created', date: '2026-07-05', done: true },
      { label: 'Sent to Grace Kim', date: '2026-07-06', done: true },
      { label: 'Opened by recipient', date: '2026-07-06', done: true },
      { label: 'Signed', date: '2026-07-07', done: true },
    ],
  },
  {
    id: 'env_6', name: 'Lease Agreement — Maple Street Duplex Unit A', template_name: 'Standard Lease Agreement',
    recipient_name: 'Aaron Cole', recipient_email: 'aaron.cole@email.com',
    property_name: 'Maple Street Duplex', unit: 'Unit A',
    status: 'declined', created_at: '2026-07-08', sent_at: '2026-07-09', completed_at: '2026-07-12',
    recipient_id: 'r_m4n5o6',
    timeline: [
      { label: 'Document created', date: '2026-07-08', done: true },
      { label: 'Sent to Aaron Cole', date: '2026-07-09', done: true },
      { label: 'Opened by recipient', date: '2026-07-11', done: true },
      { label: 'Declined — rent increase disputed', date: '2026-07-12', done: true },
    ],
  },
  {
    id: 'env_7', name: 'Month-to-Month Agreement — Riverside Townhomes Unit 9', template_name: 'Month-to-Month Rental Agreement',
    recipient_name: 'Diego Alvarez', recipient_email: 'diego.alvarez@email.com',
    property_name: 'Riverside Townhomes', unit: 'Unit 9',
    status: 'finished', created_at: '2026-06-12', sent_at: '2026-06-12', completed_at: '2026-06-14',
    recipient_id: 'r_p7q8r9', download_url: 'https://app.firma.dev/download/env_7',
    timeline: [
      { label: 'Document created', date: '2026-06-12', done: true },
      { label: 'Sent to Diego Alvarez', date: '2026-06-12', done: true },
      { label: 'Opened by recipient', date: '2026-06-13', done: true },
      { label: 'Signed', date: '2026-06-14', done: true },
    ],
  },
  {
    id: 'env_8', name: 'Security Deposit Receipt — Riverside Townhomes Unit 4', template_name: 'Security Deposit Receipt',
    recipient_name: 'Sofia Martinez', recipient_email: 'sofia.martinez@email.com',
    property_name: 'Riverside Townhomes', unit: 'Unit 4',
    status: 'expired', created_at: '2026-05-30', sent_at: '2026-05-31', completed_at: '2026-06-30',
    recipient_id: 'r_s1t2u3',
    timeline: [
      { label: 'Document created', date: '2026-05-30', done: true },
      { label: 'Sent to Sofia Martinez', date: '2026-05-31', done: true },
      { label: 'Expired after 30 days unsigned', date: '2026-06-30', done: true },
    ],
  },
];

const DUMMY_TEMPLATES: FirmaTemplate[] = [
  {
    id: 'tpl_1', name: 'Standard Lease Agreement', category: 'Lease',
    description: 'Fixed 12-month residential lease. Covers rent, deposit, utilities, maintenance responsibilities and house rules.',
    fields: 14, signers: 2, times_used: 23, last_used_at: '2026-07-22', updated_at: '2026-05-01',
  },
  {
    id: 'tpl_2', name: 'Month-to-Month Rental Agreement', category: 'Lease',
    description: 'Rolling 30-day tenancy with a 30-day notice clause for either party.',
    fields: 11, signers: 2, times_used: 8, last_used_at: '2026-06-12', updated_at: '2026-04-18',
  },
  {
    id: 'tpl_3', name: 'Pet Addendum', category: 'Addendum',
    description: 'Adds a pet to an existing lease — breed, weight limit, pet rent and refundable pet deposit.',
    fields: 7, signers: 2, times_used: 12, last_used_at: '2026-06-27', updated_at: '2026-03-22',
  },
  {
    id: 'tpl_4', name: 'Move-In / Out Checklist', category: 'Inspection',
    description: 'Room-by-room condition report signed at move-in and again at move-out, so deposit deductions are defensible.',
    fields: 26, signers: 2, times_used: 19, last_used_at: '2026-07-29', updated_at: '2026-05-12',
  },
  {
    id: 'tpl_5', name: 'Lease Renewal', category: 'Lease',
    description: 'Renewal offer with the new term and rent adjustment pre-filled from the current lease.',
    fields: 9, signers: 2, times_used: 6, last_used_at: '2026-07-18', updated_at: '2026-06-02',
  },
  {
    id: 'tpl_6', name: 'Notice to Vacate', category: 'Notice',
    description: 'Formal end-of-tenancy notice with the move-out date and deposit return timeline.',
    fields: 6, signers: 1, times_used: 4, last_used_at: '2026-07-05', updated_at: '2026-06-20',
  },
  {
    id: 'tpl_7', name: 'Security Deposit Receipt', category: 'Notice',
    description: 'Confirms the deposit amount received and where it is held, as required in most states.',
    fields: 5, signers: 1, times_used: 15, last_used_at: '2026-05-30', updated_at: '2026-04-04',
  },
];

@Injectable({
  providedIn: 'root'
})
export class FirmaService {

  private http = inject(HttpClient);
  private demoData = inject(DemoDataService);
  private useDummyData = true;

  private post(action: string, payload: any = {}): Promise<any> {
    return firstValueFrom(this.http.post(`${environment.api_url}api/firma`, { action, ...payload }));
  }

  // The Firma integration isn't live yet, so a real account has no documents and no
  // templates — showing the demo world's leases there would be fabricating their data.
  // The demo account is the only place the sample content belongs.
  private get showsDummyData(): boolean {
    return this.useDummyData && this.demoData.shouldServeTemplates();
  }

  async listEnvelopes(): Promise<FirmaEnvelope[]> {
    if (this.useDummyData) { return this.showsDummyData ? DUMMY_ENVELOPES : []; }
    return this.post('list-envelopes');
  }

  async listTemplates(): Promise<FirmaTemplate[]> {
    if (this.useDummyData) { return this.showsDummyData ? DUMMY_TEMPLATES : []; }
    return this.post('list-templates');
  }

  async sendEnvelope(payload: { template_id: string; name: string; recipient_name: string; recipient_email: string }): Promise<FirmaEnvelope> {
    if (this.useDummyData) {
      const today = new Date().toISOString().slice(0, 10);
      const envelope: FirmaEnvelope = {
        id: `env_${DUMMY_ENVELOPES.length + 1}`,
        name: payload.name,
        template_name: DUMMY_TEMPLATES.find(t => t.id === payload.template_id)?.name || 'Document',
        recipient_name: payload.recipient_name,
        recipient_email: payload.recipient_email,
        property_name: '',
        unit: '',
        status: 'in_progress',
        created_at: today,
        sent_at: today,
        recipient_id: `r_${Math.round(performance.now())}`,
        timeline: [
          { label: 'Document created', date: today, done: true },
          { label: `Sent to ${payload.recipient_name}`, date: today, done: true },
          { label: 'Opened by recipient', date: '', done: false },
          { label: 'Signed', date: '', done: false },
        ],
      };
      DUMMY_ENVELOPES.unshift(envelope);
      return envelope;
    }
    return this.post('send-envelope', payload);
  }

  signerLink(envelope: FirmaEnvelope): string {
    return `https://app.firma.dev/signing/${envelope.recipient_id}`;
  }

  // One share link per agreement: before it's signed this is the document's
  // review/sign page (recipient-agnostic, so it works even for not-sent
  // envelopes); once finished it resolves to the signed document itself.
  agreementLink(envelope: FirmaEnvelope): string {
    if (envelope.status === 'finished') {
      return envelope.download_url ?? `https://app.firma.dev/documents/${envelope.id}/signed.pdf`;
    }
    return `https://app.firma.dev/documents/${envelope.id}`;
  }

  // Templates: add / edit / remove (Firma manages templates; mocked here).
  async addTemplate(template: Partial<FirmaTemplate>): Promise<FirmaTemplate> {
    const created: FirmaTemplate = {
      id: `tpl_${DUMMY_TEMPLATES.length + 1}`,
      name: template.name || 'New Template',
      description: template.description || '',
      category: template.category || 'Lease',
      fields: template.fields ?? 0,
      signers: template.signers ?? 1,
      times_used: 0,
      updated_at: new Date().toISOString().slice(0, 10),
    };
    DUMMY_TEMPLATES.push(created);
    return created;
  }

  async removeTemplate(id: string): Promise<void> {
    const idx = DUMMY_TEMPLATES.findIndex(t => t.id === id);
    if (idx >= 0) { DUMMY_TEMPLATES.splice(idx, 1); }
  }
}
